import { XIcon } from 'lucide-react'
import { createContext, use } from 'react'

import { Drawer, IconButton } from '~/ui'
import { cn } from '~/utils/classnames'

interface SideDrawerContextValue {
  isStatic: boolean
  onClose?: () => void
}

const SideDrawerContext = createContext<SideDrawerContextValue | null>(null)

const useSideDrawerContext = () => {
  const context = use(SideDrawerContext)
  if (!context) {
    throw new Error('SideDrawer components must be used within SideDrawer')
  }
  return context
}

interface SideDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
  /**
   * If true, renders as a static positioned element (not modal).
   * Useful for desktop layouts where the drawer should take up space
   * and not block interaction with the rest of the page.
   * @default false
   */
  static?: boolean
  /**
   * Additional CSS classes to apply to the drawer container
   */
  className?: string
}

const SideDrawerRoot = ({
  open,
  onOpenChange,
  children,
  static: isStatic = false,
  className,
}: SideDrawerProps) => {
  const contextValue: SideDrawerContextValue = {
    isStatic,
    onClose: () => onOpenChange(false),
  }

  if (isStatic) {
    // Static mode: render as regular div (no modal, takes up space in DOM)
    if (!open) return null

    return (
      <SideDrawerContext value={contextValue}>
        <div
          className={cn(
            'bg-background text-foreground border-border/4 flex flex-col overflow-hidden border',
            'h-[calc(100%-80px)] w-full',
            'md:mt-2 md:mb-2 md:h-[calc(100vh-_--spacing(16))] md:w-[50vw] md:rounded-xl lg:w-[50vw] xl:w-[33vw]',
            className
          )}
        >
          {children}
        </div>
      </SideDrawerContext>
    )
  }

  // Modal mode: use Drawer (native dialog element)
  return (
    <SideDrawerContext value={contextValue}>
      <Drawer open={open} onOpenChange={onOpenChange}>
        <Drawer.Content
          className={cn(
            'flex flex-col after:content-none! focus-visible:ring-0',
            'h-[calc(100%-80px)] w-full',
            'md:!mt-2 md:!mb-2 md:h-[calc(100vh-16px)] md:w-[50vw] lg:w-[50vw] xl:w-[33vw]',
            className
          )}
        >
          {children}
        </Drawer.Content>
      </Drawer>
    </SideDrawerContext>
  )
}

interface SideDrawerHeaderProps {
  className?: string
  description?: string
  title: string
}

const SideDrawerHeader = ({
  className,
  description,
  title,
}: SideDrawerHeaderProps) => {
  const { isStatic, onClose } = useSideDrawerContext()

  if (isStatic) {
    // Static mode: render regular header (no Drawer.Title/Close)
    return (
      <div className="border-border/4 flex w-full items-center justify-between border-b py-1.5 pr-1.5 pl-3 md:pr-2">
        <div className="flex items-center gap-3">
          <h2 className={cn('text-xs font-medium', className)}>{title}</h2>
        </div>

        <IconButton
          variant="ghost"
          aria-label="Close"
          size="md"
          onClick={onClose}
        >
          <XIcon />
        </IconButton>
      </div>
    )
  }

  // Modal mode: use Drawer components
  return (
    <>
      <Drawer.Title className="sr-only">{title}</Drawer.Title>
      {description && (
        <Drawer.Description className="sr-only">
          {description}
        </Drawer.Description>
      )}

      <div className="border-border/4 flex w-full items-center justify-between border-b py-1.5 pr-1.5 pl-3 md:pr-2">
        <div className="flex items-center gap-3">
          <h2 className={cn('text-xs font-medium', className)}>{title}</h2>
        </div>

        <Drawer.Close asChild>
          <IconButton variant="ghost" aria-label="Close" size="md">
            <XIcon />
          </IconButton>
        </Drawer.Close>
      </div>
    </>
  )
}

const SideDrawerContent = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <div className={cn('flex-1 overflow-y-auto p-4', className)}>
      {children}
    </div>
  )
}

type SideDrawerComponent = typeof SideDrawerRoot & {
  Header: typeof SideDrawerHeader
  Content: typeof SideDrawerContent
}

export const SideDrawer: SideDrawerComponent = Object.assign(SideDrawerRoot, {
  Header: SideDrawerHeader,
  Content: SideDrawerContent,
})
